import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PowerTile } from "@/components/design/PowerTile";
import { ProfileSilhouette } from "@/components/design/ProfileSilhouette";
import { ELEMENT_CODES } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import {
  POWERS_WEIGHT_DISPLAY_DECIMALS,
  POWERS_WEIGHT_DISPLAY_DIVISOR,
  POWERS_WEIGHT_MAX,
  POWERS_WEIGHT_MIN,
  POWER_GROUPS,
  POWER_WEIGHT_LEVELS,
  POWER_WEIGHT_LEVEL_MESSAGES,
  powerWeightLevel,
  type PowerWeightLevel,
} from "./match-design-rules";
import type { PowerGroupKey } from "./match-result-codec";
import type { MatchData } from "./match-design-types";

interface PowerProfileEditorProps {
  profile: MatchData["teamProfile"];
  onChange: (profile: MatchData["teamProfile"]) => void;
  readOnly?: boolean;
}

// Inline team-profile editor. Renders the ProfileSilhouette as two rows of
// three cluster cells; clicking a cluster label expands its 5 powers in a
// framed strip directly below that row, keeping the briefing compact and the
// silhouette-to-detail relationship visible (#31). The strip animates open
// and closed via the grid-rows 0fr/1fr pattern (same mechanic as StatTile's
// hover actions) and carries a caret pointing at the active cluster column,
// so the strip's cluster membership stays readable after the motion ends
// (#173). Each tile carries one readout, its relevance level as a word, and
// cycles to the next level when clicked; the stored 0..1000 weight stays the
// wire representation. Edits autosave through onChange, no manual
// save/reset; re-selecting the preset in the dropdown is the reset path. See
// @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1 and CONTEXT.md [[Power Profile Editor]].
export function PowerProfileEditor({
  profile,
  onChange,
  readOnly = false,
}: PowerProfileEditorProps) {
  const intl = useIntl();
  const [activeGroup, setActiveGroup] = useState<PowerGroupKey | null>(null);
  // Nach abgeschlossener Aufklapp-Animation die Hoehenbindung loesen (gleiches
  // settled-Muster wie Disclosure). Solange grid-rows-[1fr] plus overflow-hidden
  // aktiv sind, haengt die Hoehe des Strips am Grid-Track: aendert der Inhalt
  // danach seine Hoehe — beim Verkleinern des Fensters wechselt die Spaltenzahl
  // und die aspect-square-Kacheln werden hoeher — waechst der Track nicht mit
  // und der Strip schneidet unten ab, zuerst den Stepper. Ist die Animation
  // durch, laesst auto/visible den Inhalt frei fliessen und jedes Resize wirkt.
  const [settledGroup, setSettledGroup] = useState<PowerGroupKey | null>(null);

  const weightLevelLabels = useMemo(
    () =>
      POWER_WEIGHT_LEVEL_MESSAGES.map((m) => intl.formatMessage(m)) as [
        string,
        string,
        string,
        string,
      ],
    [intl],
  );

  const ELEMENT_FROM_CODE = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [el, abbr] of Object.entries(ELEMENT_CODES)) out[abbr] = el;
    return out;
  }, []);

  const abbrWeights = useMemo(() => {
    const out: Record<string, number> = {};
    if (profile) {
      for (const [element, w] of Object.entries(profile.elementWeights)) {
        const abbr = ELEMENT_CODES[element];
        if (abbr) out[abbr] = w;
      }
    }
    return out;
  }, [profile]);

  function setWeight(abbr: string, value: number) {
    if (!profile) return;
    const element = ELEMENT_FROM_CODE[abbr];
    if (!element) return;
    const next = { ...profile.elementWeights };
    // Math.round faengt die Fliesskomma-Rueckrechnung der Anzeige ab: der
    // Stepper liefert 7.39, mal 100 ergibt in JS auch mal 738.9999999999999.
    // Gespeichert wird immer ein ganzzahliger 0..1000-Skalenpunkt, weil
    // team_profile_element.weight int4 ist und ElementWeight.weight ein
    // GraphQL-Int. Hier und nicht im Feld, damit Kachelklick und Tippen
    // dieselbe Grenze teilen.
    next[element] = Math.min(POWERS_WEIGHT_MAX, Math.max(POWERS_WEIGHT_MIN, Math.round(value)));
    onChange({ ...profile, elementWeights: next });
  }

  function toggle(key: PowerGroupKey) {
    setSettledGroup(null);
    setActiveGroup((current) => (current === key ? null : key));
  }

  // Bar taps open (never close) the cluster, so tapping around the
  // silhouette always lands on the matching strip.
  function openGroup(key: PowerGroupKey) {
    setSettledGroup(null);
    setActiveGroup(key);
  }

  const rows = [POWER_GROUPS.slice(0, 3), POWER_GROUPS.slice(3, 6)] as const;
  const weights = profile?.elementWeights ?? {};

  // Ein Cluster hat fuenf Powers, die sollen in eine Reihe. Die Mindestbreite
  // einer Spalte bestimmt dabei der Stepper, nicht die Kachel: zwei Buttons plus
  // das Feld ergeben rund 104px. Bei fuenf Spalten stehen an der @lg-Schwelle
  // (32rem) nur 98px zur Verfuegung, ab @xl (36rem) sind es 110px — deshalb
  // kommt die fuenfte Spalte erst dort, dazwischen bleiben es vier.
  // Nachgerechnet, nicht geschaetzt.
  function renderTiles(abbrs: ReadonlyArray<string>) {
    return (
      <div className="grid grid-cols-2 gap-1.5 @sm:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5">
        {abbrs.map((abbr) => {
          const p = POWER_BY_ABBR[abbr];
          if (!p) return null;
          // Wire-layer densifies on read (`match-design-mapping.ts`), so
          // `abbrWeights` always has an entry per power. The `?? 0` is a
          // defensive type-narrowing fallback for noUncheckedIndexedAccess;
          // it must match ProfileSilhouette's fallback so the bar and tile
          // can never disagree.
          const value = abbrWeights[abbr] ?? POWERS_WEIGHT_MIN;
          const level = powerWeightLevel(value);
          const tile = (
            <PowerTile
              key={abbr}
              tag={p.tag}
              abbr={abbr}
              name={p.name}
              active
              // Same hover-reveal artwork as the Power Map. No rated-gate
              // here: the unlock mechanic belongs to Grow, in the briefing
              // the artwork is a recognition aid for every power.
              imageSrc={`/powers/${abbr.toLowerCase()}.jpg`}
              weightLevel={level}
              weightLevelLabels={weightLevelLabels}
              onWeightLevelSelect={
                readOnly
                  ? undefined
                  : (next: PowerWeightLevel) => {
                      // Re-selecting the current level is a no-op so bucketed
                      // preset values (e.g. 7 shown as Mittel) keep their
                      // stored value instead of normalising to the write value.
                      if (next === level) return;
                      setWeight(abbr, POWER_WEIGHT_LEVELS[next]);
                    }
              }
              className="power-tile--white"
            />
          );

          // Feld UNTER der Kachel, nicht darin: die Kachel ist als Ganzes der
          // Cycle-Button, ein Eingabefeld darin waere eine verschachtelte
          // Interaktion (UI_CONVENTIONS, "no nested interactive area").
          // Klicken bleibt der schnelle Weg, tippen der genaue; beide schreiben
          // denselben 0..1000-Wert.
          //
          // KEIN items-center am Spaltencontainer: das setzt align-items auf
          // center, wodurch die Kachel auf ihre Inhaltsbreite schrumpft — und
          // eine aspect-square-Kachel ohne Breite hat auch keine Hoehe mehr,
          // die Bilder verschwinden. Zentriert wird deshalb nur der Stepper.
          return (
            <div key={abbr} className="flex flex-col gap-1">
              {tile}
              <NumberStepper
                // Der Stepper rechnet in der ANZEIGE-Skala (0..10 mit zwei
                // Nachkommastellen), gespeichert wird auf 0..1000. 739
                // steht hier als 7,39 und geht als 739 zurueck.
                value={value / POWERS_WEIGHT_DISPLAY_DIVISOR}
                min={POWERS_WEIGHT_MIN / POWERS_WEIGHT_DISPLAY_DIVISOR}
                max={POWERS_WEIGHT_MAX / POWERS_WEIGHT_DISPLAY_DIVISOR}
                step={1 / POWERS_WEIGHT_DISPLAY_DIVISOR}
                decimals={POWERS_WEIGHT_DISPLAY_DECIMALS}
                disabled={readOnly}
                className="flex shrink-0 justify-center"
                ariaLabel={intl.formatMessage(
                  { id: "match.weightInput.label", defaultMessage: "Gewicht für {power}" },
                  { power: p.name },
                )}
                onChange={(next) => setWeight(abbr, next * POWERS_WEIGHT_DISPLAY_DIVISOR)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex}>
          <ProfileSilhouette
            elementWeights={weights}
            groups={row}
            activeCluster={activeGroup}
            onClusterClick={toggle}
            onBarClick={openGroup}
            className="px-1"
          />
          {/* One always-mounted container per cluster so opening AND closing
              animate (grid-rows 0fr/1fr needs the content in the DOM, see
              StatTile). `inert` keeps the collapsed tiles out of the tab
              order and the a11y tree. */}
          {row.map((group, columnIndex) => {
            const open = group.key === activeGroup;
            const settled = open && settledGroup === group.key;
            // Caret center = the cluster's column center in the 3-col grid
            // above: 1/6, 3/6, 5/6 of the row width.
            const caretCenter = `${(((columnIndex * 2 + 1) / 6) * 100).toFixed(3)}%`;
            return (
              <div
                key={group.key}
                inert={!open}
                onTransitionEnd={(e) => {
                  // Nur die eigene Zeilen-Transition zaehlt, Kind-Transitions bubbeln hoch.
                  if (e.target === e.currentTarget && open) setSettledGroup(group.key);
                }}
                // transition-[grid-template-rows,opacity] statt transition-all:
                // all animiert auch Layout-Eigenschaften des Inhalts mit und kann
                // den Strip auf einer Zwischenhoehe stehen lassen.
                //
                // Die Zeile bleibt durchgehend 1fr, wie in Disclosure. Ein
                // settled-abhaengiges `auto` waere nicht animierbar und liesse das
                // Zuklappen springen; 1fr waechst mit dem Inhalt, sobald der Clip
                // darueber weg ist.
                className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
                  open ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className={settled ? "min-h-0" : "min-h-0 overflow-hidden"}>
                  <div className="relative pt-2">
                    {/* Two stacked triangles form the caret: the accent one is
                        the outline (signal color, matches the strip ring), the
                        surface one fills it and covers the ring hairline
                        underneath. */}
                    <span
                      aria-hidden
                      className="absolute top-0 border-x-8 border-b-8 border-x-transparent border-b-accent"
                      style={{ left: `calc(${caretCenter} - 8px)` }}
                    />
                    <span
                      aria-hidden
                      className="absolute top-[2px] z-10 border-x-[7px] border-b-[7px] border-x-transparent border-b-surface"
                      style={{ left: `calc(${caretCenter} - 7px)` }}
                    />
                    {/* box with the ring lifted to accent: the strip is the
                        one active editing surface on the step, so it carries
                        the signal color (same 1px weight as
                        .power-tile--active). ring-inset because the animated
                        wrapper is overflow-hidden and would clip an outset
                        ring on the left/right/bottom edges. */}
                    <div className="box ring-accent ring-inset p-2">{renderTiles(group.abbrs)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
